import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { toast } from '@hivebrite/design-system/Toast';

import { QUERY_KEYS } from 'hb-react/frontoffice/apps/Forum/constants';
import { useForumContext } from 'hb-react/frontoffice/apps/Forum/contexts/ForumContext';
import { updateDiscussion } from 'hb-react/frontoffice/apps/Forum/requests';
import type { DiscussionEditValues } from 'hb-react/frontoffice/apps/Forum/requests/requests.types';
import {
  invalidateItems,
  ITEMS_QUERY_KEYS,
} from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';

export const useUpdateDiscussion = (discussionId: number) => {
  const { configUrls } = useForumContext();
  const { topicId } = useTopicContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { isLoading, mutateAsync: mutate } = useMutation(
    (values: DiscussionEditValues) =>
      updateDiscussion({ configUrls, discussionId, values }),
    {
      onSuccess: () => {
        toast.success(__('Successfully updated'));
        invalidateItems(queryClient, [ITEMS_QUERY_KEYS.forum.items, topicId]);
        invalidateItems(queryClient, [
          ITEMS_QUERY_KEYS.forum.pinnedItems,
          topicId,
        ]);
        invalidateItems(queryClient, [ITEMS_QUERY_KEYS.forum.side, topicId]);
        queryClient.invalidateQueries([
          QUERY_KEYS.discussion,
          discussionId,
          topicId,
        ]);
        queryClient.invalidateQueries([
          QUERY_KEYS.editDiscussion,
          discussionId,
          topicId,
        ]);
        navigate(configUrls.discussionUrl(discussionId));
      },
      onError: () => {
        toast.error(__('Sorry, an error occurred'));
      },
    },
  );

  return { isLoading, mutate };
};
